import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useState } from 'react';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Accordion from '../../components/Accordion';
import AccordionBodyItem from '../../components/Accordion/AccordionBodyItem';
import AvatarGroup, {
  AvatarGroupSize,
  AvatarGroupStyle,
} from '../../components/Avatar/AvatarGroup';
import Button from '../../components/Button';
import DatePicker from '../../components/DatePicker';
import DefaultCard from '../../components/DefaultCard/DefaultCard';
import FilterTag from '../../components/FilterTag';
import FingerTipsLinks from '../../components/FingertipsLinks/FingertipsLinks';
import GenericCard from '../../components/GenericCards';
import IconButton from '../../components/IconButton';
import Label from '../../components/Label';
import Link from '../../components/Link';
import Popover from '../../components/Popover';
import Radio, { RadioGroup } from '../../components/Radio';
import Select from '../../components/Select';
import SelectableBankCard, {
  SelectableDefaultBankCard,
} from '../../components/SelectableBankCard';
import Switch from '../../components/Switch';
import useMobile from '../../hooks/useMobileHook';
import useModal from '../../hooks/useModal';
import { AuthStore } from '../../providers/auth';
import { EnrollmentProvider } from '../../providers/enrollment';
import {
  PaymentInformationProvider,
  PaymentInformationStore,
} from '../../providers/payment-information';
import {
  HEALTH_INSURANCE_ROUTE,
  PAY_NOW_ROUTE,
  PROFILE_ROUTE,
} from '../../routes/types';
import {
  addOrRemoveDaysToGivenDate,
  addOrRemoveMonthsToGivenDate,
  DateFormats,
  formatDate,
  formatPrice,
  openExternalLink,
} from '../../utils/utils';
import DependantFsaModal from '../widgets/dependantFSA/DependantFsaModal';
import FilterPopover from '../widgets/filterPopover';
import HealthFsaModal from '../widgets/healthFSA/HealthFsaModal';
import HealthSavingsModal from '../widgets/healthSavings/HealthSavingsModal';
import HospitalIndemnityModal from '../widgets/hospitalIndemnity/HospitalIndemnityModal';
import LegalServicesModal from '../widgets/legalServices/LegalServicesModal';
import BaseLifeInsuranceModal from '../widgets/lifeAndDisabilityModals/baseLifeInsurance/BaseLifeInsuranceModal';
import LongTermDisabilityServicesModal from '../widgets/lifeAndDisabilityModals/longTermDisabilityServiceModal/LongTermDisabilityServiceModal';
import OptionalLifeInsuranceModal from '../widgets/lifeAndDisabilityModals/optionalLifeInsurance/OptionalLifeInsuranceModal';
import SpouseLifeInsuranceModal from '../widgets/lifeAndDisabilityModals/spouseLifeInsurance/SpouseLifeInsuranceModal';
import MedicalBenefitsModal from '../widgets/MedicalBenefitsModal';
import NonTobaccoUserCreditModal from '../widgets/nonTabaccoUserCredit/NonTabaccoUserCreditModal';
import Medical from '../widgets/PaymentInformationTabs/Medical';
import PopoverWithLinks from '../widgets/PopoverLinks/PopoverLinks';
import AddNewBankModal from '../widgets/retireOnlineModals/addNewBankModal/AddNewBankModal';
import SpousalSurchargeModal from '../widgets/spousalSurcharge/SpousalSurchargeModal';
import CriticalIllnessModal from '../widgets/criticalIllnessModal/criticalIllnessModal';
import { ModalManagementStore } from '../../providers/modal-manager';
import { mapProfileIds } from '../../services/profiles';
import PaymentHistoryModal from '../widgets/PaymentInformation/PaymentHistoryModal';
import TulipIcon from '../../assets/tulip.svg';

const date = new Date();

const medicalCoverageHistory = mapProfileIds([
  {
    startDate: formatDate(new Date(date.getFullYear(), 0, 1), DateFormats.US),
    endDate: 'Present',
    plan: 'HMO Plan',
    profileIds: ['1', '2', '3', '4'],
  },

  {
    startDate: formatDate(
      new Date(date.getFullYear() - 1, 0, 1),
      DateFormats.US
    ),
    endDate: formatDate(
      new Date(date.getFullYear() - 1, 11, 31),
      DateFormats.US
    ),
    plan: 'HMO Plan',
    profileIds: ['1', '2'],
  },

  {
    startDate: formatDate(
      new Date(date.getFullYear() - 2, 0, 1),
      DateFormats.US
    ),
    endDate: formatDate(
      new Date(date.getFullYear() - 2, 11, 31),
      DateFormats.US
    ),
    plan: 'PPO Plan',
    profileIds: ['1'],
  },

  {
    startDate: formatDate(
      new Date(date.getFullYear() - 3, 0, 1),
      DateFormats.US
    ),
    endDate: formatDate(
      new Date(date.getFullYear() - 3, 11, 31),
      DateFormats.US
    ),
    plan: 'PPO Plan',
    profileIds: ['1'],
  },
]);

const dentalCoverageHistory = mapProfileIds([
  {
    startDate: formatDate(new Date(date.getFullYear(), 0, 1), DateFormats.US),
    endDate: 'Present',
    plan: 'PPO Plan',
    profileIds: ['1', '2', '3', '4'],
  },

  {
    startDate: formatDate(
      new Date(date.getFullYear() - 1, 0, 1),
      DateFormats.US
    ),
    endDate: formatDate(
      new Date(date.getFullYear() - 1, 11, 31),
      DateFormats.US
    ),
    plan: 'PPO Plan',
    profileIds: ['1', '2'],
  },

  {
    startDate: formatDate(
      new Date(date.getFullYear() - 2, 0, 1),
      DateFormats.US
    ),
    endDate: formatDate(
      new Date(date.getFullYear() - 2, 11, 31),
      DateFormats.US
    ),
    plan: 'DMO Plan',
    profileIds: ['1'],
  },

  {
    startDate: formatDate(
      new Date(date.getFullYear() - 3, 0, 1),
      DateFormats.US
    ),
    endDate: formatDate(
      new Date(date.getFullYear() - 3, 11, 31),
      DateFormats.US
    ),
    plan: 'DMO Plan',
    profileIds: ['1'],
  },
]);

const linksData = [
  {
    linkHeader: 'Verification of Coverage',
    route: 'verification-of-coverage',
    modal: true,
  },
];

const eventConfirmationOptions = [
  {
    value: `${formatDate(
      new Date(date.getFullYear() - 1, 0, 1),
      DateFormats.US
    )} Annual Enrollement`,
    label: `${formatDate(
      new Date(date.getFullYear() - 1, 0, 1),
      DateFormats.US
    )} Annual Enrollement`,
  },
  {
    value: `${formatDate(
      new Date(date.getFullYear() - 2, 3, 4),
      DateFormats.US
    )} New Child`,
    label: `${formatDate(
      new Date(date.getFullYear() - 2, 3, 4),
      DateFormats.US
    )} New Child`,
  },
  {
    value: `${formatDate(
      new Date(date.getFullYear() - 2, 0, 1),
      DateFormats.US
    )} Annual Enrollement`,
    label: `${formatDate(
      new Date(date.getFullYear() - 2, 0, 1),
      DateFormats.US
    )} Annual Enrollement`,
  },
  {
    value: `${formatDate(
      new Date(date.getFullYear() - 3, 6, 1),
      DateFormats.US
    )} Married`,
    label: `${formatDate(
      new Date(date.getFullYear() - 3, 6, 1),
      DateFormats.US
    )} Married`,
  },
  {
    value: `${formatDate(
      new Date(date.getFullYear() - 3, 0, 1),
      DateFormats.US
    )} Annual Enrollement`,
    label: `${formatDate(
      new Date(date.getFullYear() - 3, 0, 1),
      DateFormats.US
    )} Annual Enrollement`,
  },
  {
    value: `${formatDate(
      new Date(date.getFullYear() - 4, 11, 1),
      DateFormats.US
    )} New Hire`,
    label: `${formatDate(
      new Date(date.getFullYear() - 4, 11, 1),
      DateFormats.US
    )} New Hire`,
  },
];

const specificBenefitOptions = [
  { value: 'Select One', label: 'Select One' },
  { value: 'Medical', label: 'Medical' },
  { value: 'Dental', label: 'Dental' },
  { value: 'Basic Life Insurance', label: 'Basic Life Insurance' },
  {
    value: 'Basic Accidental Death & Dismemberment',
    label: 'Basic Accidental Death & Dismemberment',
  },
  { value: 'Long Term Disability', label: 'Long Term Disability' },
  {
    value: 'Spouse/Domestic Partner Life Insurance',
    label: 'Spouse/Domestic Partner Life Insurance',
  },
  { value: 'Child Life Insurance', label: 'Child Life Insurance' },
  {
    value: 'Optional Accidental Death & Dismemberment',
    label: 'Optional Accidental Death & Dismemberment',
  },
  { value: 'Vision', label: 'Vision' },
  { value: 'Optional Life Insurance', label: 'Optional Life Insurance' },
  { value: 'Disability', label: 'Disability' },
  { value: 'Salary Continuation', label: 'Salary Continuation' },
  { value: 'Health Care FSA', label: 'Health Care FSA' },
  { value: 'Dependent Care FSA', label: 'Dependent Care FSA' },
  {
    value: 'Health Savings Account (HSA)',
    label: 'Health Savings Account (HSA)',
  },
];

const tabsData = [
  {
    name: 'Medical',
    content: <Medical />,
    leftIcon: <FontAwesomeIcon icon={light('heart-pulse')} />,
  },
  {
    name: 'Dental',
    content: <div>Email</div>,
    leftIcon: <FontAwesomeIcon icon={light('tooth')} />,
  },
  {
    name: 'Vision',
    content: <div>Phone</div>,
    leftIcon: <FontAwesomeIcon icon={light('glasses')} />,
  },
];

const recentPayments: any[] = [
  {
    statement: formatDate(
      addOrRemoveMonthsToGivenDate(new Date(), -1),
      DateFormats.M_YEAR
    ),
    coverageDates: `${formatDate(
      new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      DateFormats.US
    )} - ${formatDate(
      new Date(new Date().getFullYear(), new Date().getMonth(), 0),
      DateFormats.US
    )}`,
    amoutDue: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    paymentReceived: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    method: 'Credit Card',
    dateConfig: {
      year: new Date().getFullYear(),
      month: new Date(
        new Date().getFullYear(),
        new Date().getMonth() - 1,
        1
      ).getMonth(),
    },
  },

  {
    statement: formatDate(
      addOrRemoveMonthsToGivenDate(new Date(), -2),
      DateFormats.M_YEAR
    ),
    coverageDates: `${formatDate(
      new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
      DateFormats.US
    )} - ${formatDate(
      new Date(new Date().getFullYear(), new Date().getMonth() - 1, 0),
      DateFormats.US
    )}`,
    amoutDue: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    paymentReceived: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    method: 'Credit Card',
    dateConfig: {
      year: new Date().getFullYear(),
      month: new Date(
        new Date().getFullYear(),
        new Date().getMonth() - 1,
        1
      ).getMonth(),
    },
  },

  {
    statement: formatDate(
      addOrRemoveMonthsToGivenDate(new Date(), -3),
      DateFormats.M_YEAR
    ),
    coverageDates: `${formatDate(
      new Date(new Date().getFullYear(), new Date().getMonth() - 2, 1),
      DateFormats.US
    )} - ${formatDate(
      new Date(new Date().getFullYear(), new Date().getMonth() - 2, 0),
      DateFormats.US
    )}`,
    amoutDue: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    paymentReceived: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    method: 'Credit Card',
    dateConfig: {
      year: new Date().getFullYear(),
      month: new Date(
        new Date().getFullYear(),
        new Date().getMonth() - 2,
        1
      ).getMonth(),
    },
  },
];

const paymentHistory: any[] = [
  {
    statement: 'Nov 2022',
    coverageDates: `${formatDate(
      new Date(new Date().getFullYear(), 11, 1),
      DateFormats.US
    )} - ${formatDate(
      new Date(new Date().getFullYear(), 11, 31),
      DateFormats.US
    )}`,
    amoutDue: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    paymentReceived: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    method: 'Credit Card',
    dateConfig: {
      year: new Date().getFullYear(),
      month: new Date(new Date().getFullYear(), 9, 1).getMonth(),
    },
  },

  {
    statement: 'Oct 2022',
    coverageDates: `${formatDate(
      new Date(new Date().getFullYear(), 10, 1),
      DateFormats.US
    )} - ${formatDate(
      new Date(new Date().getFullYear(), 10, 30),
      DateFormats.US
    )}`,
    amoutDue: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    paymentReceived: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    method: 'Credit Card',
    dateConfig: {
      year: new Date().getFullYear(),
      month: new Date(new Date().getFullYear(), 7, 1).getMonth(),
    },
  },

  {
    statement: 'Sep 2022',
    coverageDates: `${formatDate(
      new Date(new Date().getFullYear(), 9, 1),
      DateFormats.US
    )} - ${formatDate(
      new Date(new Date().getFullYear(), 9, 31),
      DateFormats.US
    )}`,
    amoutDue: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    paymentReceived: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    method: 'Bank Account',
    dateConfig: {
      year: new Date().getFullYear(),
      month: new Date(new Date().getFullYear(), 8, 1).getMonth(),
    },
  },

  {
    statement: 'Aug 2022',
    coverageDates: `${formatDate(
      new Date(new Date().getFullYear(), 8, 1),
      DateFormats.US
    )} - ${formatDate(
      new Date(new Date().getFullYear(), 8, 30),
      DateFormats.US
    )}`,
    amoutDue: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    paymentReceived: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    method: 'Credit Card',
    dateConfig: {
      year: new Date().getFullYear(),
      month: new Date(new Date().getFullYear(), 7, 1).getMonth(),
    },
  },
  {
    statement: 'Jul 2022',
    coverageDates: `${formatDate(
      new Date(new Date().getFullYear(), 7, 1),
      DateFormats.US
    )} - ${formatDate(
      new Date(new Date().getFullYear(), 7, 31),
      DateFormats.US
    )}`,
    amoutDue: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    paymentReceived: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    method: 'Credit Card',
    dateConfig: {
      year: new Date().getFullYear(),
      month: new Date(new Date().getFullYear(), 7, 1).getMonth(),
    },
  },
  {
    statement: 'Jun 2022',
    coverageDates: `${formatDate(
      new Date(new Date().getFullYear(), 6, 1),
      DateFormats.US
    )} - ${formatDate(
      new Date(new Date().getFullYear(), 6, 31),
      DateFormats.US
    )}`,
    amoutDue: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    paymentReceived: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    method: 'Bank Account',
    dateConfig: {
      year: new Date().getFullYear(),
      month: new Date(new Date().getFullYear(), 7, 1).getMonth(),
    },
  },
  {
    statement: 'May 2022',
    coverageDates: `${formatDate(
      new Date(new Date().getFullYear(), 5, 1),
      DateFormats.US
    )} - ${formatDate(
      new Date(new Date().getFullYear(), 5, 30),
      DateFormats.US
    )}`,
    amoutDue: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    paymentReceived: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    method: 'Credit Card',
    dateConfig: {
      year: new Date().getFullYear(),
      month: new Date(new Date().getFullYear(), 7, 1).getMonth(),
    },
  },
  {
    statement: 'Apr 2022',
    coverageDates: `${formatDate(
      new Date(new Date().getFullYear(), 4, 1),
      DateFormats.US
    )} - ${formatDate(
      new Date(new Date().getFullYear(), 4, 31),
      DateFormats.US
    )}`,
    amoutDue: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    paymentReceived: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    method: 'Credit Card',
    dateConfig: {
      year: new Date().getFullYear(),
      month: new Date(new Date().getFullYear(), 7, 1).getMonth(),
    },
  },
  {
    statement: 'Mar 2022',
    coverageDates: `${formatDate(
      new Date(new Date().getFullYear(), 3, 1),
      DateFormats.US
    )} - ${formatDate(
      new Date(new Date().getFullYear(), 3, 30),
      DateFormats.US
    )}`,
    amoutDue: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    paymentReceived: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    method: 'Bank Account',
    dateConfig: {
      year: new Date().getFullYear(),
      month: new Date(new Date().getFullYear(), 7, 1).getMonth(),
    },
  },
  {
    statement: 'Feb 2022',
    coverageDates: `${formatDate(
      new Date(new Date().getFullYear(), 2, 1),
      DateFormats.US
    )} - ${formatDate(
      new Date(new Date().getFullYear(), 2, 31),
      DateFormats.US
    )}`,
    amoutDue: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    paymentReceived: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    method: 'Credit Card',
    dateConfig: {
      year: new Date().getFullYear(),
      month: new Date(new Date().getFullYear(), 7, 1).getMonth(),
    },
  },
  {
    statement: 'Jan 2022',
    coverageDates: `${formatDate(
      new Date(new Date().getFullYear(), 1, 1),
      DateFormats.US
    )} - ${formatDate(
      new Date(new Date().getFullYear(), 1, 28),
      DateFormats.US
    )}`,
    amoutDue: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    paymentReceived: formatPrice(332, 'en-US', {
      currency: 'USD',
      style: 'currency',
      minimumFractionDigits: 2,
    }),
    method: 'Credit Card',
    dateConfig: {
      year: new Date().getFullYear(),
      month: new Date(new Date().getFullYear(), 7, 1).getMonth(),
    },
  },
];

const banksData = {
  selectedBankOption: '1',
  selectedBank: {
    id: '1',
    name: 'Wells Fargo Bank',
    iban: '1234123412',
    routingNumber: '034802454',
    accountNumber: '43214321',
    type: 'Savings',
  },
  banksOptions: [
    {
      id: '1',
      name: 'Wells Fargo Bank',
      iban: '1234123412',
      routingNumber: '034802454',
      accountNumber: '43214321',
      type: 'Savings',
    },
    {
      id: '2',
      name: 'Bank of America',
      iban: '4321432143',
      routingNumber: '034804444',
      accountNumber: '12341234',
      type: 'Cheking',
    },
  ],
};

const separateStringAndMakeFirstLetterUppercase = (str: string) => {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getCardIcon = (type: String) => {
  switch (type) {
    case 'heart-pulse':
      return light('heart-pulse');
    case 'tooth':
      return light('tooth');
    case 'glasses':
      return light('glasses');
    case 'face-thermometer':
      return light('face-thermometer');
    case 'family':
      return light('family');
    case 'bed-pulse':
      return light('bed-pulse');
    case 'hospitals':
      return light('hospitals');
    case 'gavel':
      return light('gavel');
    case 'clipboard-medical':
      return light('clipboard-medical');
    default:
      return light('image-slash');
  }
};

const getPlanCostStyle = (type: string) => {
  switch (type) {
    case 'owed':
      return 'default';
    case 'credit':
      return 'green';
    default:
      return 'disabled';
  }
};

const getAlertButton = (alertInfo: any[], handleAlertClick: any) => {
  const alertBody = (
    <Col>
      <Row className="pb-7 m-0 fw-normal">
        {alertInfo && alertInfo[0].title}
      </Row>

      <Row className="pb-7 m-0 fw-light lh-xs">
        {alertInfo && alertInfo[0].description}
      </Row>

      <Col className="m-0 mt-3 d-flex justify-content-end">
        <Button
          onClick={() => handleAlertClick(alertInfo[0])}
          size="sm"
          variant="primary"
        >
          Complete
        </Button>
      </Col>
    </Col>
  );

  return (
    <Popover placement="top-end" popoverBody={alertBody}>
      <IconButton
        variant="icon-alert"
        icon={light('circle-exclamation')}
        size={'sm'}
      />
    </Popover>
  );
};

const PERSONAS_WITHOUT_PAYMENT = [
  'persona-hw',
  'persona-hw-db',
  'persona-hw-dc',
  'persona-hw-db-dc',
  'persona-hw-db-dc-hq',
  'persona-hw-db-dc-midyear',
  'persona-dc',
];

const PaymentInformationView = () => {
  const [] = useContext(AuthStore) || [];
  const {
    fingertipLinks,
    activeHealthBenefits,
    activeLifeAndDisabilityInsurances,
    activeLifestyleBenefits,
    activeSpendingAccounts,
    currentLifeEvent,
    currentPersona,
  } = useContext(PaymentInformationStore) || {};
  const navigate = useNavigate();

  const handleAlertClick = (info: any) => {
    if (!info.route) {
      return;
    }
    if (info.file || info.target === '_href') {
      window.open(`${process.env.PUBLIC_URL}/${info.route}`);
      return;
    }
    if (info.modal) {
      openModal(info.route);
      document.body.click();
      return;
    }

    navigate(info.route);
  };

  const [data, setData] = useState(banksData);
  const [showModal, handleShowModal, handleCloseModal] = useModal();
  const [
    showPaymentHistoryModal,
    handleShowPaymentHistoryModal,
    handleClosePaymentHistoryModal,
  ] = useModal();
  const [showManageAutoPay, setShowManageAutoPay] = useState<boolean>(false);
  const [enableAutoPay, setEnableAutoPay] = useState<boolean>(false);
  const [switched, setSwitched] = useState(false);
  const [filteringData, setFilteringData] = useState<any>({
    filterOption: undefined,
    filterInfo: undefined,
  });
  const [filterData, setFilterData] = useState<any>({
    filterOption: undefined,
    filterInfo: undefined,
  });
  const [expanded, setExpanded] = useState([
    { activeKey: '1', id: 0 },
    { activeKey: '1', id: 1 },
    { activeKey: '1', id: 2 },
    { activeKey: '1', id: 3 },
  ]);

  const handleBankOptionChange = (e: any) => {
    setData({
      ...data,
      selectedBankOption: e,
    });
  };

  const handleNewBank = (bank: any) => {
    setData({
      ...data,
      banksOptions: [...data.banksOptions, bank],
    });
  };

  const handleOnSave = () => {
    const selectedBank = data.banksOptions.filter(
      (bank) => data.selectedBankOption === bank.id
    );

    setData({
      ...data,
      selectedBank: selectedBank[0],
    });

    setShowManageAutoPay(false);
  };

  const [medicalSelectedPlanRadio, setMedicalSelectedPlanRadio] = useState<
    string | number
  >(1);

  const [shouldShowCurrentValue] = useState<boolean>(false);

  const [
    showHealthFSAModal,
    handleShowHealthFSAModal,
    handleCloseHealthFSAModal,
  ] = useModal();
  const [
    showDependedFSAModal,
    handleShowDependedFSAModal,
    handleCloseDependedFSAModal,
  ] = useModal();
  const [
    showSpouseLifeInsuranceModal,
    handleShowSpouseLifeInsuranceModal,
    handleCloseSpouseLifeInsuranceModal,
  ] = useModal();
  const [
    showOptionalLifeInsuranceModal,
    handleShowOptionalLifeInsuranceModal,
    handleCloseOptionalLifeInsuranceModal,
  ] = useModal();
  const [
    showBaseLifeInsuranceModal,
    handleShowBaseLifeInsuranceModal,
    handleCloseBaseLifeInsuranceModal,
  ] = useModal();
  const [
    showLongTermDisabilityServicesModal,
    handleShowLongTermDisabilityServicesModal,
    handleCloseLongTermDisabilityServicesModal,
  ] = useModal();
  const [
    showCriticalIllnessModal,
    handleShowCriticalIllnessModal,
    handleCloseCriticalIllnessModal,
  ] = useModal();
  const [
    showHealthSavingsModal,
    handleShowHealthSavingsModal,
    handleCloseHealthSavingsModal,
  ] = useModal();
  const [
    showSpousalSurchargeModal,
    handleShowSpousalSurchargeModal,
    handleCloseSpousalSurchargeModal,
  ] = useModal();
  const [
    showNonTobaccoUserCreditModal,
    handleShowNonTobaccoUserCreditModal,
    handleCloseNonTobaccoUserCreditModal,
  ] = useModal();
  const [
    showHospitalIndemnityModal,
    handleShowHospitalIndemnityModal,
    handleCloseHospitalIndemnityModal,
  ] = useModal();
  const [
    showLegalServicesModal,
    handleShowLegalServicesModal,
    handleCloseLegalServicesModal,
  ] = useModal();

  const [
    showMedicalBenefitsModal,
    handleShowMedicalBenefitsModal,
    handleCloseMedicalBenefitModal,
  ] = useModal();

  const { openModal } = useContext(ModalManagementStore);

  const getButtonModal = (type: string) => {
    switch (type) {
      case 'base-life':
        return handleShowBaseLifeInsuranceModal;
      case 'optional-life':
        return handleShowOptionalLifeInsuranceModal;
      case 'spouse-life':
        return handleShowSpouseLifeInsuranceModal;
      case 'long-term-disability':
        return handleShowLongTermDisabilityServicesModal;
      case 'critical-illness':
        return handleShowCriticalIllnessModal;
      case 'health-care':
        return handleShowHealthFSAModal;
      case 'dependant-care':
        return handleShowDependedFSAModal;
      case 'spousal-surcharge':
        return handleShowSpousalSurchargeModal;
      case 'non-tobacco-user-credit':
        return handleShowNonTobaccoUserCreditModal;
      case 'health-savings':
        return handleShowHealthSavingsModal;
      case 'hospital-indemnity':
        return handleShowHospitalIndemnityModal;
      case 'legal-services':
        return handleShowLegalServicesModal;
      case 'medical':
        return handleShowMedicalBenefitsModal;
    }
  };

  const dateDeadline = formatDate(addOrRemoveDaysToGivenDate(new Date(), 14));

  const isMobile = useMobile();

  const handleExpanded = () => {
    setSwitched((prev) => !prev);

    const tempExp = [...expanded];
    tempExp.forEach((x) => {
      if (switched) {
        x.activeKey = '1';

        return;
      }

      x.activeKey = '0';
    });

    setExpanded((prev) => (prev = tempExp));
  };

  const handleExpandStandalone = (idx: number) => {
    const temp = [...expanded];
    temp.forEach((x) => {
      if (x.id === idx) {
        if (x.activeKey === '0') {
          x.activeKey = '1';
          setSwitched((prev) => (prev = false));

          return;
        }

        x.activeKey = '0';
      }
    });

    setExpanded((prev) => (prev = temp));
  };

  const handleFilterOption = (e: string | number) => {
    setFilteringData({ filterOption: e });
  };

  const handleFilterInfo = (e: any) => {
    const localFormatStr = e.toLocaleString().split(', ')[0];

    setFilteringData({
      ...filteringData,
      filterInfo: localFormatStr.match(/^\d{2}\/\d{2}\/\d{4}$/)
        ? formatDate(e)
        : e,
    });
  };

  const handleFilter = () => {
    setFilterData(
      (prev: any) =>
        (prev = {
          filterOption: filteringData.filterOption,
          filterInfo: filteringData.filterInfo,
        })
    );
    setFilteringData((prev: any) => (prev = filteringData));
  };

  const handleRemoveTag = () => {
    setFilterData(
      (prev: any) => (prev = { filterOption: undefined, filterInfo: undefined })
    );
  };

  const handleReset = () => {
    setFilteringData(
      (prev: any) => (prev = { filterOption: undefined, filterInfo: undefined })
    );
  };

  const getFilteredData = () => {
    if (filterData.filterInfo === 'Medical')
      return (
        <Container>
          {medicalCoverageHistory.map((benefit: any, index: number) => (
            <Row
              key={index}
              className={`mx-0 py-3 align-items-center ${
                index !== medicalCoverageHistory.length - 1
                  ? 'border-bottom-light-gray-lg'
                  : ''
              }`}
            >
              <Col className={'ps-0 col-4'}>
                <span
                  className={'text-fs-6 text-dark-gray fw-light'}
                >{`${benefit.startDate} - ${benefit.endDate}`}</span>
              </Col>

              <Col className={'col-4'}>
                <span className={'text-fs-6 text-dark-gray fw-light'}>
                  {benefit.plan}
                </span>
              </Col>

              <Col className={'pe-0 col-4'}>
                <AvatarGroup
                  avatars={benefit.profiles}
                  groupSize={AvatarGroupSize.MINIMIZED}
                  groupStyle={AvatarGroupStyle.STACKED}
                  hasTooltip
                />
              </Col>
            </Row>
          ))}
        </Container>
      );
    else if (filterData.filterInfo === 'Dental')
      return (
        <Container>
          {dentalCoverageHistory.map((benefit: any, index: number) => (
            <Row
              key={index}
              className={`mx-0 py-3 align-items-center ${
                index !== medicalCoverageHistory.length - 1
                  ? 'border-bottom-light-gray-lg'
                  : ''
              }`}
            >
              <Col className={'ps-0 col-4'}>
                <span
                  className={'text-fs-6 text-dark-gray fw-light'}
                >{`${benefit.startDate} - ${benefit.endDate}`}</span>
              </Col>

              <Col className={'col-4'}>
                <span className={'text-fs-6 text-dark-gray fw-light'}>
                  {benefit.plan}
                </span>
              </Col>

              <Col className={'pe-0 col-4'}>
                <AvatarGroup
                  avatars={benefit.profiles}
                  groupSize={AvatarGroupSize.MINIMIZED}
                  groupStyle={AvatarGroupStyle.STACKED}
                  hasTooltip
                />
              </Col>
            </Row>
          ))}
        </Container>
      );

    return (
      <Container>
        <Row>
          <Accordion
            onHandleExpand={() => handleExpandStandalone(0)}
            activeKey={expanded[0].activeKey}
            title={'Health Benefits'}
          >
            {activeHealthBenefits.map((benefit: any, index: number) => (
              <AccordionBodyItem
                key={index}
                infoIcon={getCardIcon(
                  benefit['generic'] && benefit['generic']['icon']
                )}
                title={benefit['generic']['title']}
                subtitle={
                  benefit['plan'] ? benefit['plan']['title'] : 'No Coverage'
                }
                price={
                  benefit['plan']
                    ? formatPrice(Math.abs(benefit['plan']['cost']))
                    : '$0.00'
                }
                originalPrice={
                  benefit['plan']
                    ? formatPrice(Math.abs(benefit['plan']['originalCost']))
                    : '$0.00'
                }
                isOriginalPriceShown={shouldShowCurrentValue}
                priceStyleType={getPlanCostStyle(
                  benefit['plan']['cost'] === 0
                    ? ''
                    : benefit['plan'] && benefit['plan']['costType']
                )}
                isHint={benefit['generic']['hint']}
                avatarGroup={
                  benefit['generic']['familyMembers'] && (
                    <AvatarGroup
                      avatars={benefit.profiles}
                      groupSize={AvatarGroupSize.MINIMIZED}
                      groupStyle={AvatarGroupStyle.STACKED}
                      hasTooltip
                    />
                  )
                }
                buttonOnMouseDown={getButtonModal(benefit['type'])}
              />
            ))}
            <MedicalBenefitsModal
              radioButtonValue={medicalSelectedPlanRadio}
              setRadioButtonValue={setMedicalSelectedPlanRadio}
              showMedicalBenefitModal={showMedicalBenefitsModal}
              handleCloseMedicalBenefitModal={handleCloseMedicalBenefitModal}
            />
            <HealthSavingsModal
              showHealthSavingsModal={showHealthSavingsModal}
              handleCloseHealthSavingsModal={handleCloseHealthSavingsModal}
            />
            <SpousalSurchargeModal
              showSpousalSurchargeModal={showSpousalSurchargeModal}
              handleCloseSpousalSurchargeModal={
                handleCloseSpousalSurchargeModal
              }
            />
            <NonTobaccoUserCreditModal
              showNonTobaccoUserCreditModal={showNonTobaccoUserCreditModal}
              handleCloseNonTobaccoUserCreditModal={
                handleCloseNonTobaccoUserCreditModal
              }
            />
            <CriticalIllnessModal
              showCriticalIllnessModal={showCriticalIllnessModal}
              handleCloseCriticalIllnessModal={handleCloseCriticalIllnessModal}
            />
            <HospitalIndemnityModal
              showHospitalIndemnityModal={showHospitalIndemnityModal}
              handleCloseHospitalIndemnityModal={
                handleCloseHospitalIndemnityModal
              }
            />
          </Accordion>
        </Row>
        <Row className="mt-10">
          <Accordion
            onHandleExpand={() => handleExpandStandalone(2)}
            activeKey={expanded[2].activeKey}
            title={'Spending Accounts'}
          >
            {activeSpendingAccounts.map((benefit: any, index: number) => (
              <AccordionBodyItem
                key={index}
                infoIcon={getCardIcon(
                  benefit['generic'] && benefit['generic']['icon']
                )}
                infoIconVariant={'icon-default'}
                title={
                  !benefit['generic']
                    ? separateStringAndMakeFirstLetterUppercase(benefit['type'])
                    : benefit['generic']['title']
                }
                subtitle={
                  benefit['plan'] ? benefit['plan']['title'] : 'No Coverage'
                }
                price={
                  benefit['plan']
                    ? formatPrice(benefit['plan']['cost'])
                    : '$0.00'
                }
                originalPrice={
                  benefit['plan']
                    ? formatPrice(benefit['plan']['originalCost'])
                    : '$0.00'
                }
                isOriginalPriceShown={shouldShowCurrentValue}
                priceStyleType={getPlanCostStyle(benefit['plan']['costType'])}
                buttonOnMouseDown={getButtonModal(benefit['type'])}
              />
            ))}
            <HealthFsaModal
              showHealthFSAModal={showHealthFSAModal}
              handleCloseHealthFSAModal={handleCloseHealthFSAModal}
            />
            <DependantFsaModal
              showDependedFSAModal={showDependedFSAModal}
              handleCloseDependedFSAModal={handleCloseDependedFSAModal}
            />
          </Accordion>
        </Row>
        <Row className="mt-10">
          <Accordion
            onHandleExpand={() => handleExpandStandalone(3)}
            activeKey={expanded[3].activeKey}
            title={'Life & Disability Insurance'}
            infoIcon={light('circle-exclamation')}
          >
            {activeLifeAndDisabilityInsurances.map(
              (benefit: any, index: number) => (
                <AccordionBodyItem
                  key={index}
                  infoIcon={getCardIcon(
                    benefit['generic'] && benefit['generic']['icon']
                  )}
                  title={
                    !benefit['generic']
                      ? separateStringAndMakeFirstLetterUppercase(
                          benefit['type']
                        )
                      : benefit['generic']['title']
                  }
                  infoIconVariant={'icon-default'}
                  subtitle={
                    !benefit['plan']['id'].includes('noCoverage')
                      ? `${benefit['plan']['title']}${
                          benefit['plan']['coverage']
                            ? `, ${formatPrice(
                                benefit['plan']['coverage'],
                                'en-US',
                                {
                                  currency: 'USD',
                                  style: 'currency',
                                  minimumFractionDigits: 0,
                                }
                              )}`
                            : ''
                        }`
                      : 'No Coverage'
                  }
                  price={
                    benefit['plan']
                      ? formatPrice(benefit['plan']['cost'])
                      : '$0.00'
                  }
                  originalPrice={
                    benefit['plan']
                      ? formatPrice(benefit['plan']['originalCost'])
                      : '$0.00'
                  }
                  infoButton={
                    benefit['alerts'] &&
                    getAlertButton(benefit['alerts'], handleAlertClick)
                  }
                  isOriginalPriceShown={shouldShowCurrentValue}
                  priceStyleType={getPlanCostStyle(
                    benefit['plan'] && benefit['plan']['costType']
                  )}
                  buttonOnMouseDown={getButtonModal(benefit['type'])}
                />
              )
            )}
            <BaseLifeInsuranceModal
              showBaseLifeInsuranceModal={showBaseLifeInsuranceModal}
              handleCloseBaseLifeInsuranceModal={
                handleCloseBaseLifeInsuranceModal
              }
            />
            <OptionalLifeInsuranceModal
              showOptionalLifeInsuranceModal={showOptionalLifeInsuranceModal}
              handleCloseOptionalLifeInsuranceModal={
                handleCloseOptionalLifeInsuranceModal
              }
            />
            <SpouseLifeInsuranceModal
              showSpouseLifeInsuranceModal={showSpouseLifeInsuranceModal}
              handleCloseSpouseLifeInsuranceModal={
                handleCloseSpouseLifeInsuranceModal
              }
            />
            <LongTermDisabilityServicesModal
              showLongTermDisabilityServicesModal={
                showLongTermDisabilityServicesModal
              }
              handleCloseLongTermDisabilityServicesModal={
                handleCloseLongTermDisabilityServicesModal
              }
            />
          </Accordion>
        </Row>
        <Row className="mt-10">
          <Accordion
            onHandleExpand={() => handleExpandStandalone(1)}
            activeKey={expanded[1].activeKey}
            title={'Lifestyle Benefits'}
          >
            {activeLifestyleBenefits.map((benefit: any, index: number) => (
              <AccordionBodyItem
                key={index}
                infoIcon={getCardIcon(
                  benefit['generic'] && benefit['generic']['icon']
                )}
                title={
                  !benefit['generic']
                    ? separateStringAndMakeFirstLetterUppercase(benefit['type'])
                    : benefit['generic']['title']
                }
                infoIconVariant={'icon-default'}
                subtitle={
                  benefit['plan'] ? benefit['plan']['title'] : 'No Coverage'
                }
                price={
                  benefit['plan']
                    ? formatPrice(benefit['plan']['cost'])
                    : '$0.00'
                }
                originalPrice={
                  benefit['plan']
                    ? formatPrice(benefit['plan']['originalCost'])
                    : '$0.00'
                }
                isOriginalPriceShown={shouldShowCurrentValue}
                priceStyleType={getPlanCostStyle(
                  benefit['plan'] && benefit['plan']['costType']
                )}
                buttonOnMouseDown={getButtonModal(benefit['type'])}
              />
            ))}
            <LegalServicesModal
              showLegalServicesModal={showLegalServicesModal}
              handleCloseLegalServicesModal={handleCloseLegalServicesModal}
            />
          </Accordion>
        </Row>
      </Container>
    );
  };

  return (
    <Container className="cnd-custom-container-large mb-12 px-lg-9 px-xl-0">
      <Row className={'mt-7 mt-lg-13'}>
        <Col
          sm={12}
          lg={8}
          className={
            'd-flex justify-content-between align-items-center pt-lg-8'
          }
        >
          <h4 className={'h4 fw-light mb-0'}>{'Current Benefits'}</h4>
          <div className={'d-flex align-items-center ms-3'}>
            <Popover
              placement="bottom-end"
              popoverBody={
                <Col className="ps-1 pt-2 pe-5 pb-10">
                  <Row className="m-0 fw-normal">
                    Evidence of Insurability Required
                  </Row>
                  <Row className="m-0 mt-4 fw-light lh-xs">
                    You have 30 days to complete Evidence of Insurability (EOI)
                    for Employee Supplemental Life Insurance. A copy of this
                    form will be mailed to your address on file. You must
                    complete this form and return it to the insurance carrier
                    for underwriting review. Until approved, you will remain
                    enrolled in Waive Coverage coverage at a cost of $0.00
                    per-pay-period.
                  </Row>
                  <Col className="m-0 mt-3 d-flex justify-content-end">
                    <Button
                      onClick={() => {
                        openModal('eoi-complete-modal');
                        document.body.click();
                      }}
                      size="sm"
                      variant="primary"
                    >
                      Complete
                    </Button>
                  </Col>
                </Col>
              }
            >
              <IconButton
                variant="icon-alert"
                icon={light('info-circle')}
                className={'me-2'}
              />
            </Popover>
            <Label className="px-4 me-7 d-none d-lg-flex text-ligther-grey">
              1 Alert
            </Label>

            <PopoverWithLinks linksData={linksData} />
          </div>
        </Col>
      </Row>
      <Row className={'mt-10'}>
        <Col sm={12} lg={8}>
          <GenericCard
            className={'p-10 p-lg-11'}
            variant="white"
            style={{ zIndex: '1000' }}
          >
            <Row className={'w-100 fw-light m-0 justify-content-between'}>
              <Col
                className={
                  'px-0 py-7 py-lg-0 col-12 col-lg-6 d-flex flex-column justify-content-center border-bottom-mobile order-2 order-lg-1'
                }
              >
                <Row
                  className={
                    'flex-row flex-lg-column align-items-center align-items-lg-start'
                  }
                >
                  <Col className={'col-6 text-dark text-sm-12-lg-14 lh-xxs'}>
                    {'Effective date'}
                  </Col>

                  <Col
                    className={
                      'col-6 text-dark-gray fw-normal text-sub mt-0 mt-lg-4 lh-xxs text-end text-lg-start'
                    }
                  >
                    {formatDate(new Date(), DateFormats.MONTH_DAY_YEAR)}
                  </Col>
                </Row>
              </Col>

              <Col
                className={
                  'col-12 col-lg-6 pb-7 pb-lg-0 border-bottom-mobile order-1 order-lg-2'
                }
              >
                <Row className={'flex-row flex-lg-column'}>
                  <Col
                    className={
                      'px-0 text-dark-gray text-sm-12-lg-14 d-flex justify-content-lg-end'
                    }
                  >
                    <Label
                      className={
                        'text-dark-gray text-sm-12-lg-14 text-lg-end p-0'
                      }
                      rightIcon={light('circle-exclamation')}
                      tooltip={
                        <div className="px-1">
                          <div className="d-flex justify-content-between py-3 border-bottom-dark-gray">
                            <span className="me-5">Before-Tax</span>
                            <span>{`${formatPrice(Number(650.91), 'en-US', {
                              currency: 'USD',
                              style: 'currency',
                              minimumFractionDigits: 2,
                            })}`}</span>
                          </div>
                          <div className="d-flex justify-content-between py-3">
                            <span>After-Tax</span>
                            <span>{`${formatPrice(Number(17.5), 'en-US', {
                              currency: 'USD',
                              style: 'currency',
                              minimumFractionDigits: 2,
                            })}`}</span>
                          </div>
                        </div>
                      }
                    >
                      {'Total Cost'}
                    </Label>
                  </Col>

                  <Col className={'text-end'}>
                    <div className={'text-primary fs-5 my-4 text-lg-end'}>
                      {formatPrice(668.41)}
                    </div>
                    <div
                      className={
                        'text-light-grey text-fs-6 fw-light text-lg-end'
                      }
                    >
                      {`per-pay-period`}
                    </div>
                  </Col>
                </Row>
              </Col>

              <Col className={'col-12 order-3 px-0 pt-7 pt-lg-0 d-flex'}>
                <Container
                  style={{ width: 'fit-content' }}
                  className={'px-0 me-0 ms-auto me-lg-auto ms-lg-0'}
                >
                  <Link
                    target={'_blank'}
                    href={`${
                      process.env.PUBLIC_URL
                    }/${'blank-demo-example.pdf'}`}
                    className={'p-0'}
                    isBlue
                  >
                    Download/Print
                  </Link>
                </Container>
              </Col>
            </Row>
          </GenericCard>
          <GenericCard style={{ marginTop: '-15px' }}>
            <Row className="mt-10">
              <Col className={'col-12 mb-7 mb-lg-10'}>
                <Row className={'align-items-center'}>
                  <Col className={'col-6'}>
                    
                  </Col>
                  <Col className={'col-6 d-flex justify-content-end'}>
                    <FilterPopover
                      filterDisabled={filteringData.filterInfo === undefined}
                      onFilter={handleFilter}
                      onReset={handleReset}
                      popoverBody={
                        <Container className={'px-0'}>
                          <h5 className={'lh-xs mb-6 bolder'}>
                            Coverage History
                          </h5>
                          <RadioGroup
                            name={'coverage-history'}
                            value={filteringData.filterOption}
                            onChange={handleFilterOption}
                          >
                            <Row>
                              <Col className={'col-12'}>
                                <Radio
                                  id={'all-benefits'}
                                  value={'All Benefits'}
                                  label={'All Benefits'}
                                />
                              </Col>
                              {filteringData.filterOption === 'All Benefits' ? (
                                <Col className={'col-12 ps-13 mt-4'}>
                                  <DatePicker
                                    label={
                                      <span className="fw-light text-dark-gray-50 text-caption">
                                        View coverage from a certain point in
                                        time.
                                      </span>
                                    }
                                    onChange={handleFilterInfo}
                                  />
                                </Col>
                              ) : (
                                <></>
                              )}
                              <Col
                                style={{ maxWidth: 226 }}
                                className={
                                  'col-12 ms-13 pb-10 border-bottom-light-gray-lg'
                                }
                              >
                                {''}
                              </Col>
                            </Row>

                            <Row>
                              <Col className={'col-12 pt-8'}>
                                <Radio
                                  id={'event-confirmation'}
                                  value={'Event Confirmation'}
                                  label={'Event Confirmation'}
                                />
                              </Col>
                              {filteringData.filterOption ===
                              'Event Confirmation' ? (
                                <Col
                                  style={{ maxWidth: 226 }}
                                  className={'px-0 ms-13'}
                                >
                                  <Select
                                    minWidth={'226'}
                                    label={
                                      'View a prior life event confirmation statement.'
                                    }
                                    onChange={handleFilterInfo}
                                    options={eventConfirmationOptions}
                                  />
                                </Col>
                              ) : (
                                <></>
                              )}
                              <Col
                                style={{ maxWidth: 226 }}
                                className={
                                  'col-12 ms-13 pb-10 border-bottom-light-gray-lg'
                                }
                              >
                                {''}
                              </Col>
                            </Row>
                            <Row>
                              <Col className={'col-12 pt-8'}>
                                <Radio
                                  id={'specific-benefit'}
                                  value={'Specific Benefit'}
                                  label={'Specific Benefit'}
                                />
                              </Col>
                              {filteringData.filterOption ===
                              'Specific Benefit' ? (
                                <Col
                                  style={{ maxWidth: 226 }}
                                  className={'px-0 ms-13'}
                                >
                                  <Select
                                    minWidth={'226'}
                                    label={'View history of any benefit.'}
                                    onChange={handleFilterInfo}
                                    options={specificBenefitOptions}
                                  />
                                </Col>
                              ) : (
                                <></>
                              )}
                            </Row>
                          </RadioGroup>
                        </Container>
                      }
                      children={
                        <Button size={'sm'} leftIcon={light('filter')}>
                          Coverage History
                        </Button>
                      }
                    />
                  </Col>
                </Row>
              </Col>
              {filterData !== undefined ? (
                <Col className={'col-12'}>
                  <Row className={'mb-7'}>
                    <Col className={'col-auto'}>
                      {filterData.filterOption !== undefined ? (
                        <FilterTag
                          filter={filterData}
                          onClearFilter={handleRemoveTag}
                        />
                      ) : (
                        <></>
                      )}
                    </Col>
                  </Row>
                </Col>
              ) : (
                <></>
              )}
              {getFilteredData()}
            </Row>
          </GenericCard>
          {!PERSONAS_WITHOUT_PAYMENT.includes(currentPersona) ? (
            <>
              <Row className={'mt-7 mt-lg-13'}>
                <Col
                  sm={12}
                  lg={12}
                  className={
                    'd-flex justify-content-between align-items-center pt-lg-8'
                  }
                >
                  <h4 className={'h4 fw-light mb-0'}>
                    {'Payment Information'}
                  </h4>
                  <div
                    className={'d-flex align-items-center ms-3'}
                    role={'button'}
                    onClick={() => openModal('payment-schedule')}
                  >
                    <IconButton
                      variant={'icon-default'}
                      icon={light('calendar-day')}
                      className={'me-2 text-ligther-grey'}
                    />
                    <Label className="px-4 me-7 d-none d-lg-flex ">
                      Payment Schedule
                    </Label>
                  </div>
                </Col>
              </Row>
              <Row className={'mt-10'}>
                <Col sm={12} lg={12}>
                  <GenericCard variant="white" style={{ zIndex: '1000' }}>
                    <Row
                      className={'w-100 fw-light m-0 justify-content-between'}
                    >
                      <Col className={'col-6 p-7 p-lg-0'}>
                        <div
                          className={
                            'text-dark-gray text-sm-12-lg-14 text-lg-start'
                          }
                        >
                          <Label
                            className={
                              'text-dark-gray text-sm-12-lg-14 text-lg-start p-0'
                            }
                            rightIcon={light('circle-exclamation')}
                          >
                            {'Total Due'}
                          </Label>
                        </div>
                        <div
                          className={'text-dark-gray fs-5 my-4 text-lg-start'}
                        >
                          {formatPrice(332.0)}
                        </div>
                        <div
                          className={`text-fs-6 fw-light text-lg-start ${
                            showManageAutoPay
                              ? 'text-ligther-grey'
                              : 'text-primary'
                          }`}
                          role={'button'}
                          onClick={() => setShowManageAutoPay(true)}
                        >
                          <span>Manage Auto-Pay</span>
                        </div>
                      </Col>
                      <Col
                        className={
                          'col-6 p-7 p-lg-0 d-flex align-items-center justify-content-end'
                        }
                      >
                        <Button
                          variant="primary"
                          onClick={() => navigate(PAY_NOW_ROUTE)}
                        >
                          Pay Now
                        </Button>
                      </Col>
                    </Row>
                    {showManageAutoPay && (
                      <Row
                        className={
                          'mt-7 mt-lg-10 p-7 p-lg-10 w-100 fw-light m-0 justify-content-between gray-nested-background rounded-1'
                        }
                      >
                        <h5 className="m-0 p-0">Manage Auto-Pay</h5>
                        <Row className="m-0 p-0 pt-1 pb-7 mb-10  mb-lg-12 pb-lg-10 border-bottom d-flex flex-column flex-lg-row">
                          <Col className="p-0 col-lg-9">
                            <div className="p-0 text-sub1 fw-light text-ligther-grey lh-xxs">
                              <span>
                                With Auto-Pay you don't need to manually make
                                your payments every month.
                              </span>
                            </div>
                          </Col>
                          <Col className="p-0 mt-7 mt-lg-0 d-lg-flex justify-content-end">
                            <div>
                              <Switch
                                value="xl"
                                variant="sm"
                                labelColor="blue-label"
                                label={'Enable Autopay'}
                                id="s1"
                                isLeftLabel
                                onChange={() =>
                                  setEnableAutoPay(!enableAutoPay)
                                }
                                checked={enableAutoPay}
                              />
                            </div>
                          </Col>
                        </Row>
                        {enableAutoPay && (
                          <Row className="m-0 p-0 pt-1 mb-lg-12">
                            <h6 className="m-0 p-0 text-sm-16-lg-20">
                              Select from an existing account or add a new one
                            </h6>
                            <Container className="p-0 pt-10 d-flex flex-wrap">
                              <RadioGroup
                                name={'banks'}
                                value={data.selectedBankOption}
                                onChange={handleBankOptionChange}
                              >
                                {data.banksOptions.map(
                                  (bank: any, i: number) => (
                                    <SelectableBankCard
                                      key={i}
                                      item={bank}
                                      className={'mb-7 mb-lg-0 me-lg-5'}
                                      selected={
                                        data.selectedBankOption === bank.id
                                      }
                                    />
                                  )
                                )}
                              </RadioGroup>
                              <SelectableDefaultBankCard
                                onClick={handleShowModal}
                              />
                              <AddNewBankModal
                                showModal={showModal}
                                handleClose={handleCloseModal}
                                handleAddNewBank={handleNewBank}
                                banks={data.banksOptions}
                              />
                            </Container>
                            <h6 className="m-0 p-0 pt-7 pt-lg-12 text-sm-16-lg-20">
                              How would you like to receive Reminders?
                            </h6>
                            <div className="p-0 fw-light text-sub1 lh-xxs pt-7">
                              <span>
                                Based on your
                                <span className={'d-inline-block'}>
                                  <Link
                                    href={`#${PROFILE_ROUTE.replace(
                                      ':tab',
                                      'communication'
                                    )}`}
                                    className={
                                      'px-1 py-0 d-flex justify-content-space-between'
                                    }
                                    isBlue
                                  >
                                    Communication Center Preferences
                                  </Link>
                                </span>
                                , you'll receive a text message within 10 days
                                of your bill being due.
                              </span>
                            </div>
                          </Row>
                        )}
                        <Container className="p-0 pt-10 pt-lg-0 modal-footer">
                          <Button
                            variant="primary-outline"
                            className={'order-1 order-lg-3 mt-7 mt-lg-0'}
                            onClick={() => setShowManageAutoPay(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            className={'order-1 order-lg-3 mt-7 mt-lg-0'}
                            onClick={handleOnSave}
                          >
                            Save
                          </Button>
                        </Container>
                      </Row>
                    )}
                    <Row
                      className={`m-0 mt-7 pt-7 d-lg-flex flex-lg-row justify-content-between ${
                        !isMobile ? 'border-top' : ''
                      } `}
                    >
                      <Row
                        className={`m-0 ${
                          isMobile ? 'border-bottom' : ''
                        } px-0 py-3 py-lg-0 col-lg-3 flex-lg-column`}
                      >
                        <Col className="p-0">
                          <span className="fw-light text-sub1 lh-xxs text-ligther-grey">
                            Current Balance
                          </span>
                        </Col>
                        <Col className="p-0 text-end text-lg-start">
                          <span className="fw-normal text-sub1 lh-xxs">
                            {formatPrice(332.0)}
                          </span>
                        </Col>
                      </Row>
                      <Row
                        className={`m-0 ${
                          isMobile ? 'border-bottom' : ''
                        } px-0 py-3 py-lg-0 col-lg-3 flex-lg-column`}
                      >
                        <Col className="p-0">
                          <span className="fw-light text-sub1 lh-xxs text-ligther-grey">
                            Past Due
                          </span>
                        </Col>
                        <Col className="p-0 text-end text-lg-start">
                          <span className="fw-normal text-sub1 lh-xxs">
                            {formatPrice(0.0)}
                          </span>
                        </Col>
                      </Row>
                      <Row
                        className={`m-0 ${
                          isMobile ? 'border-bottom' : ''
                        } px-0 py-3 py-lg-0 col-lg-3 flex-lg-column`}
                      >
                        <Col className="p-0">
                          <span className="fw-light text-sub1 lh-xxs text-ligther-grey">
                            Due date
                          </span>
                        </Col>
                        <Col className="p-0 text-end text-lg-start">
                          <span className="fw-normal text-sub1 lh-xxs">
                            {formatDate(
                              addOrRemoveMonthsToGivenDate(
                                new Date(
                                  new Date().getFullYear(),
                                  new Date().getMonth(),
                                  1
                                ),
                                1
                              ),
                              DateFormats.SHORT_MONTH_DAY_YEAR
                            )}
                          </span>
                        </Col>
                      </Row>
                      <Row className="m-0 px-0 py-3 py-lg-0 col-lg-2 flex-lg-column">
                        <Col className="p-0 text-lg-end">
                          <span className="fw-light text-sub1 lh-xxs text-ligther-grey">
                            Statement
                          </span>
                        </Col>
                        <Col className="p-0 text-end text-lg-end">
                          <span className="fw-normal text-sub1 lh-xxs text-primary">
                            {formatDate(new Date(), DateFormats.M_YEAR)}
                          </span>
                        </Col>
                      </Row>
                    </Row>
                  </GenericCard>
                  <GenericCard
                    className={'d-none d-lg-block'}
                    style={{ marginTop: '-15px' }}
                  >
                    <Row>
                      <Col>
                        <h2 className={'text-sm-20-lg-24 fw-light'}>
                          Recent Payments
                        </h2>
                      </Col>
                      <Col className={'text-end'}>
                        <Button
                          variant={'primary-outline'}
                          onClick={handleShowPaymentHistoryModal}
                        >
                          Payment History
                        </Button>
                        <PaymentHistoryModal
                          showModal={showPaymentHistoryModal}
                          handleClose={handleClosePaymentHistoryModal}
                          paymentHistoryData={paymentHistory}
                        />
                      </Col>
                    </Row>

                    <Row className={'mt-10 py-3 border-bottom-dark-gray'}>
                      <Col className={'col-3'}>
                        <span className={'text-fs-6 fw-normal'}>
                          Statement/Receipt
                        </span>
                      </Col>
                      <Col className={'col-3'}>
                        <span className={'text-fs-6 fw-normal'}>
                          Coverage Dates
                        </span>
                      </Col>
                      <Col className={'col-2 text-end'}>
                        <span className={'text-fs-6 fw-normal'}>
                          Amount Due
                        </span>
                      </Col>
                      <Col className={'col-2 px-0 text-end'}>
                        <span className={'text-fs-6 fw-normal'}>
                          Payment Received
                        </span>
                      </Col>
                      <Col className={'col-2 text-end'}>
                        <span className={'text-fs-6 fw-normal'}>Method</span>
                      </Col>
                    </Row>

                    {recentPayments.map((recentPayment, idx) => (
                      <Row
                        className={`py-3 ${
                          idx !== recentPayments.length - 1
                            ? 'border-bottom-light-gray-lg'
                            : ''
                        }`}
                      >
                        <Col className={'col-3'}>
                          <span
                            className="text-fs-6 fw-light text-blue pointer-hover"
                            onClick={() =>
                              openModal('payment-information-statement', {
                                dateConfig: recentPayment.dateConfig,
                              })
                            }
                          >
                            {recentPayment.statement}
                          </span>
                        </Col>
                        <Col className={'col-3'}>
                          <span className={'text-fs-6 fw-light'}>
                            {recentPayment.coverageDates}
                          </span>
                        </Col>
                        <Col className={'col-2 text-end'}>
                          <span className={'text-fs-6 fw-light'}>
                            {recentPayment.amoutDue}
                          </span>
                        </Col>
                        <Col className={'col-2 px-0 text-end'}>
                          <span className={'text-fs-6 fw-light'}>
                            {recentPayment.paymentReceived}
                          </span>
                        </Col>
                        <Col className={'col-2 text-end'}>
                          <span className={'text-fs-6 fw-light'}>
                            {recentPayment.method}
                          </span>
                        </Col>
                      </Row>
                    ))}
                  </GenericCard>

                  <Container className={'px-0 d-block d-lg-none'}>
                    <Accordion
                      title={'Recent Payments'}
                      button={
                        <Button variant={'primary-outline'}>History</Button>
                      }
                    >
                      {recentPayments.map((recentPayment, idx) => (
                        <Row
                          className={`py-7 w-100 ${
                            idx !== recentPayments.length - 1
                              ? 'border-bottom-light-gray-lg'
                              : ''
                          }`}
                        >
                          <Col className={'col-12 px-7'}>
                            <Row className={'py-5 mx-0'}>
                              <Col className={'col-6'}>
                                <span className={'text-fs-6 fw-normal'}>
                                  Statement/Receipt
                                </span>
                              </Col>

                              <Col className={'col-6 text-end'}>
                                <span
                                  className={'text-fs-6 fw-light text-blue'}
                                >
                                  {recentPayment.statement}
                                </span>
                              </Col>
                            </Row>
                          </Col>

                          <Col className={'col-12 px-7'}>
                            <Row className={'py-5 mx-0 table-row-background'}>
                              <Col className={'col-5'}>
                                <span className={'text-fs-6 fw-normal'}>
                                  Coverage Dates
                                </span>
                              </Col>

                              <Col className={'col-7 text-end'}>
                                <span className={'text-fs-6 fw-light'}>
                                  {recentPayment.coverageDates}
                                </span>
                              </Col>
                            </Row>
                          </Col>

                          <Col className={'col-12 px-7'}>
                            <Row className={'py-5 mx-0'}>
                              <Col className={'col-6'}>
                                <span className={'text-fs-6 fw-normal'}>
                                  Amount Due
                                </span>
                              </Col>

                              <Col className={'col-6 text-end'}>
                                <span className={'text-fs-6 fw-light'}>
                                  {recentPayment.amoutDue}
                                </span>
                              </Col>
                            </Row>
                          </Col>

                          <Col className={'col-12 px-7'}>
                            <Row className={'py-5 mx-0 table-row-background'}>
                              <Col className={'col-8'}>
                                <span className={'text-fs-6 fw-normal'}>
                                  Payment Received
                                </span>
                              </Col>

                              <Col className={'col-4 text-end'}>
                                <span className={'text-fs-6 fw-light'}>
                                  {recentPayment.paymentReceived}
                                </span>
                              </Col>
                            </Row>
                          </Col>

                          <Col className={'col-12 px-7'}>
                            <Row className={'py-5 mx-0'}>
                              <Col className={'col-6'}>
                                <span className={'text-fs-6 fw-normal'}>
                                  Method
                                </span>
                              </Col>
                              <Col className={'col-6 text-end'}>
                                <span className={'text-fs-6 fw-light'}>
                                  {recentPayment.method}
                                </span>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      ))}
                    </Accordion>
                  </Container>
                </Col>
              </Row>
            </>
          ) : (
            <></>
          )}
        </Col>
        <Col sm={12} lg={4} className={'mt-11 mt-lg-0'}>
          <DefaultCard
            variant="blue"
            leftHeading={'Current Events'}
            leftSubheading={
              <>
                <div className="text-sm-12-lg-14">
                  <strong>{`${currentLifeEvent['type']}`}</strong>
                </div>
                <div className="text-sm-12-lg-14 fw-light">{`Deadline: ${dateDeadline}`}</div>
                <div className="text-sm-12-lg-14 fw-light">{`Status: ${currentLifeEvent['status']}`}</div>
              </>
            }
            button={
              <Button
                onClick={() => navigate(HEALTH_INSURANCE_ROUTE)}
                variant="primary-shadow"
                size="sm"
              >
                Edit
              </Button>
            }
          />
          <Col className="mt-12 col-12">
            <FingerTipsLinks data={fingertipLinks} />
          </Col>
          <Col className="mt-10 px-0 col-12" style={{ maxWidth: '384px' }}>
            <Card
              key={'Card1'}
              className={'doc-upload p-5 cursor-pointer card-body'}
              onClick={() => {
                openExternalLink('https://start.myalex.com/placeholder-industries/');
              }}
            >
              <section style={{ background: 'white' }}>
                <div className={'carousel-img-container rounded-1 overflow-hidden'}>
                  <img
                    className={'w-100 h-auto'}
                    src={`${process.env.PUBLIC_URL}/company10.png`}
                    alt={`${process.env.PUBLIC_URL}/company10.png`}
                    style={{ width: '100%' }}
                  />
                </div>
                <div className='mt-4 lh-xs fw-light'>
                  <h6 className='mb-0 text-sm-16-lg-20'>Let alex® help</h6>
                </div>
                <div className='lh-xxs fw-light text-sub1 text-light-grey-normal'>
                  <span>Compare your options</span>
                </div>
              </section>
            </Card>
          </Col>
          {/* <Col className="mt-12 col-12">
            <GenericCard variant="white">
              <h4 className={'h4 fw-light mb-0'}>{'Doctor Contact'}</h4>
              <Row className="m-0 mt-10">
                <Tabs tabs={tabsData} className={'p-0'} />
              </Row>
            </GenericCard>
          </Col> */}
        </Col>
      </Row>
    </Container>
  );
};

const PaymentInformation = () => {
  return (
    <EnrollmentProvider>
      <PaymentInformationProvider>
        <PaymentInformationView />
      </PaymentInformationProvider>
    </EnrollmentProvider>
  );
};

export default PaymentInformation;
